/**
 * All the state behind the Workflows page: catalog, folders, workflows and runs.
 *
 * Watching a queued run is NOT here any more. The run is asynchronous (the POST
 * only says it was queued) and takes minutes, so a poller owned by this hook
 * died the moment the user navigated off /templates and the completion went
 * unreported. `context/WorkflowRunNotifier` owns that watch app-wide now; this
 * hook hands it the run and reads the run lists and running-state back out of
 * it, so there is exactly one poll loop per run.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FolderOption } from "../components/Workflows/FolderScopePicker";
import type { WorkflowToastMessage } from "../components/Workflows/WorkflowToast";
import {
	describeCompletion,
	useWorkflowRunNotifier,
} from "../context/WorkflowRunNotifier";
import { type FolderTreeNode, getUserFolderTree } from "../services/fileApi";
import {
	ActiveRunConflictError,
	createWorkflow as createWorkflowRequest,
	listMetricGroups,
	listRuns,
	listWorkflows,
	runWorkflow,
	cancelRun,
	restoreRunVersion,
} from "../services/kpiWorkflowService";
import {
	type CreateWorkflowPayload,
	type MetricGroup,
	type Workflow,
	type WorkflowRun,
	isTerminalRunStatus,
} from "../types/kpiWorkflow";
import { shouldSoftAskForNotifications } from "../utils/desktopNotification";

/**
 * Every folder in the tree, flattened, roots included.
 *
 * Roots were skipped here originally, on the assumption that a root was some
 * storage-level container above the folders a user cares about. In this data
 * model it is the opposite: a project/dealroom IS a root folder
 * (`folder_service.create_folder` sets `is_root = parent_folder_id is None`, and
 * the project-creation path always passes `None`), so every folder a user
 * recognises was being filtered out and the picker rendered "No folders yet"
 * for a client with a full portfolio.
 *
 * Nested folders are kept too and labelled with their ancestry ("Harborview /
 * 2026"), because a bare child name is ambiguous once two dealrooms both have a
 * "Statements". Selecting a parent already covers its children on the backend
 * (`store.folder_ancestry`), so the child entries are a narrowing tool, not a
 * requirement.
 */
export function flattenFolders(nodes: FolderTreeNode[]): FolderOption[] {
	const out: FolderOption[] = [];
	const walk = (list: FolderTreeNode[], trail: string[]) => {
		for (const node of list) {
			const path = [...trail, node.folder.name];
			out.push({
				id: node.folder.id,
				name: path.join(" / "),
				fileCount: node.folder.file_count,
			});
			walk(node.sub_folders, path);
		}
	};
	walk(nodes, []);
	return out;
}

export function useKpiWorkflows() {
	const [metricGroups, setMetricGroups] = useState<MetricGroup[]>([]);
	const [folders, setFolders] = useState<FolderOption[]>([]);
	const [workflows, setWorkflows] = useState<Workflow[]>([]);
	const [loadedRuns, setLoadedRuns] = useState<
		Record<string, WorkflowRun[]>
	>({});
	const [isLoading, setIsLoading] = useState(true);
	const [foldersLoading, setFoldersLoading] = useState(true);
	const [foldersError, setFoldersError] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [pendingRunIds, setPendingRunIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [loadingRunsFor, setLoadingRunsFor] = useState<Set<string>>(
		() => new Set(),
	);
	const [loadError, setLoadError] = useState<Error | null>(null);
	const [toast, setToast] = useState<WorkflowToastMessage | null>(null);

	// The app-level watch. It owns the polling for any run this page queues,
	// which is why nothing here polls: `notifierRuns` is the fresher truth for a
	// workflow being watched, and `trackedWorkflowIds` is what "running" means
	// once the POST has returned.
	const {
		trackRun,
		untrackRun,
		adoptRuns,
		trackedWorkflowIds,
		runsByWorkflow: notifierRuns,
		settlements,
		adoptWorkflows,
		isHydrating,
	} = useWorkflowRunNotifier();

	const isMounted = useRef(true);
	/** Monotonic id of the newest folder-tree request; older ones are ignored. */
	const folderRequest = useRef(0);
	/**
	 * The same, per workflow, for run-history requests (DOP-276).
	 *
	 * Four paths now read a history — the user expanding a card, a settlement
	 * drain, a cancel, and a return to the tab — so two can easily be in flight
	 * at once. Without a token the slower one lands last and wins, painting
	 * older rows over newer ones.
	 */
	const runsRequest = useRef<Map<string, number>>(new Map());
	/** And for the workflow list, which the same paths also refetch. */
	const workflowsRequest = useRef(0);

	/**
	 * Claim the newest run-history request for a workflow; the returned
	 * predicate says whether this response is still the one worth keeping.
	 */
	const beginRunsRequest = useCallback((workflowId: string) => {
		const token = (runsRequest.current.get(workflowId) ?? 0) + 1;
		runsRequest.current.set(workflowId, token);
		return () =>
			isMounted.current && runsRequest.current.get(workflowId) === token;
	}, []);

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	const markRunning = useCallback((workflowId: string, running: boolean) => {
		setPendingRunIds((current) => {
			const next = new Set(current);
			if (running) {
				next.add(workflowId);
			} else {
				next.delete(workflowId);
			}
			return next;
		});
	}, []);

	const refreshWorkflows = useCallback(async () => {
		const token = workflowsRequest.current + 1;
		workflowsRequest.current = token;
		const next = await listWorkflows();
		// Every successful read of the list is a hydration source: `last_run`
		// tells the notifier what is already in flight, and this read may be the
		// only one that succeeded. Adoption happens even for a superseded
		// response: a live run it names is live whichever read saw it first, and
		// the notifier de-duplicates by run id. Only the RENDERED list is
		// ordering-sensitive.
		adoptWorkflows(next);
		if (isMounted.current && workflowsRequest.current === token) {
			setWorkflows(next);
		}
	}, [adoptWorkflows]);

	const loadRuns = useCallback(
		async (workflowId: string) => {
			setLoadingRunsFor((current) => new Set(current).add(workflowId));
			const isCurrent = beginRunsRequest(workflowId);
			try {
				const runs = await listRuns(workflowId);
				// Arm the app-level watch on what this history just revealed
				// (DOP-276): a history is the other place a live run becomes
				// visible, and a row nobody is watching sits frozen at whatever it
				// said when it loaded. Done for a superseded response too, for the
				// reason given in `refreshWorkflows`.
				adoptRuns(workflowId, runs);
				if (isCurrent()) {
					setLoadedRuns((current) => ({ ...current, [workflowId]: runs }));
				}
				return runs;
			} finally {
				if (isMounted.current) {
					setLoadingRunsFor((current) => {
						const next = new Set(current);
						next.delete(workflowId);
						return next;
					});
				}
			}
		},
		[adoptRuns, beginRunsRequest],
	);

	/**
	 * Fetch the folder tree beside the page load rather than inside it.
	 *
	 * The tree is the slowest of the page's three requests and the only consumer
	 * is the composer's folder picker, so awaiting it held the whole page behind
	 * a spinner for something most visits never look at. It is still kicked off
	 * on mount, so the picker is usually warm by the time a composer opens; while
	 * it is not, `foldersLoading` lets the picker say so.
	 *
	 * A failure is reported as `foldersError`, never as the page-level
	 * `loadError`, and it does NOT clear folders that were already loaded: a
	 * failed refresh leaves the last good list in place, because a stale list a
	 * user can still tick beats an empty one. The picker therefore only shows
	 * the error when it has nothing else to show — with a Retry that calls back
	 * in here rather than asking for a page reload.
	 */
	const loadFolders = useCallback(() => {
		const requestId = folderRequest.current + 1;
		folderRequest.current = requestId;
		setFoldersLoading(true);
		setFoldersError(false);
		// `isCurrent` drops a response from a superseded request (a retry pressed
		// while the first is still in flight), which otherwise lands last and wins.
		const isCurrent = () =>
			isMounted.current && folderRequest.current === requestId;
		void getUserFolderTree({ includeFiles: false })
			.then((tree) => {
				if (!isCurrent()) return;
				setFolders(flattenFolders(tree));
				setFoldersError(false);
			})
			.catch(() => {
				// Not a page-level `loadError`: nothing else on the page needs
				// folders, and failing the whole page over the picker is worse
				// than a composer that cannot narrow by folder. But it is not
				// silence either — an outage rendered as "No folders yet" tells
				// the user something false about their own account.
				if (isCurrent()) setFoldersError(true);
			})
			.finally(() => {
				if (isCurrent()) setFoldersLoading(false);
			});
	}, []);

	const reload = useCallback(async () => {
		setIsLoading(true);
		setLoadError(null);
		loadFolders();
		try {
			const [groups, existing] = await Promise.all([
				listMetricGroups(),
				listWorkflows(),
			]);
			adoptWorkflows(existing);
			if (!isMounted.current) return;
			setMetricGroups(groups);
			setWorkflows(existing);
		} catch (error) {
			if (isMounted.current) {
				setLoadError(
					error instanceof Error
						? error
						: new Error("Could not load your workflows."),
				);
			}
		} finally {
			if (isMounted.current) setIsLoading(false);
		}
	}, [loadFolders, adoptWorkflows]);

	// Same guard as the KPI table/custom-metrics hooks: StrictMode's dev
	// remount re-runs this effect with the SAME `reload`, and without it every
	// mount pays a duplicate fan-out (metric groups + workflows, plus the
	// background folder tree). `reload` is stable for the hook's lifetime, so
	// this fires exactly once; consumers refetch through `reload` directly.
	const lastAutoReloadRef = useRef<typeof reload | null>(null);
	useEffect(() => {
		if (lastAutoReloadRef.current === reload) return;
		lastAutoReloadRef.current = reload;
		void reload();
	}, [reload]);

	const createWorkflow = useCallback(
		async (payload: CreateWorkflowPayload): Promise<Workflow | null> => {
			setIsSaving(true);
			try {
				const workflow = await createWorkflowRequest(payload);
				if (isMounted.current) {
					setWorkflows((current) => [workflow, ...current]);
					setToast({
						text: `"${workflow.name}" is ready. Run it now, or wait for the next file to arrive.`,
						variant: "success",
					});
				}
				return workflow;
			} catch (error) {
				if (isMounted.current) {
					setToast({
						text:
							error instanceof Error
								? error.message
								: "Could not save this workflow.",
						variant: "error",
					});
				}
				return null;
			} finally {
				if (isMounted.current) setIsSaving(false);
			}
		},
		[],
	);

	/**
	 * Refresh what the page shows about a workflow after its run state changed.
	 *
	 * Reported separately from whatever caused the change: a run that IS queued
	 * and a page that could not redraw itself are two different pieces of news,
	 * and collapsing them into one error message told the user their extraction
	 * had not started when it had.
	 */
	const refreshAfterRunChange = useCallback(
		async (workflowId: string): Promise<boolean> => {
			const results = await Promise.allSettled([
				loadRuns(workflowId),
				refreshWorkflows(),
			]);
			return results.every((result) => result.status === "fulfilled");
		},
		[loadRuns, refreshWorkflows],
	);

	const runNow = useCallback(
		async (workflowId: string, force = false) => {
			markRunning(workflowId, true);
			let run: WorkflowRun;
			try {
				run = await runWorkflow(workflowId, { force });
			} catch (error) {
				markRunning(workflowId, false);
				if (error instanceof ActiveRunConflictError) {
					// Not a failure. The backend holds one active run per workflow
					// (DOP-269), and the user asked for an extraction while one was
					// already going — so watch THAT one and say so. Reporting this as
					// an error would tell them nothing is happening when something is.
					if (error.activeRun) trackRun(workflowId, error.activeRun.id);
					setToast({
						text: "A run for this workflow is already in progress. Watching that one.",
						variant: "info",
					});
					await refreshAfterRunChange(workflowId);
					return;
				}
				setToast({
					text:
						error instanceof Error
							? error.message
							: "Could not start this run.",
					variant: "error",
				});
				return;
			}

			// The enqueue succeeded, so the watch is registered BEFORE anything
			// else is awaited. It used to be the last step after two more reads,
			// and a failure in either of those threw the whole call into the catch
			// above: the run was reported as never started, and nothing ever
			// watched the extraction that was in fact running.
			if (isTerminalRunStatus(run.status)) {
				markRunning(workflowId, false);
				setToast(describeCompletion(run));
			} else {
				markRunning(workflowId, false);
				trackRun(workflowId, run.id);
				// The run-start toast is where the app OFFERS to notify — it does
				// not prompt. Chrome suppresses a prompt with no trusted gesture
				// behind it, and an ignored offer is cheaper than a reflexive deny.
				setToast({
					text: force
						? "Re-extraction queued. Every file will be read again."
						: "Extraction queued.",
					variant: "info",
					softAsk: shouldSoftAskForNotifications(),
				});
			}

			const refreshed = await refreshAfterRunChange(workflowId);
			if (!refreshed && isMounted.current) {
				setToast({
					text: "The run was started, but this page could not refresh. Reload to see its progress.",
					variant: "warning",
				});
			}
		},
		[markRunning, refreshAfterRunChange, trackRun],
	);

	const cancelRunNow = useCallback(
		async (workflowId: string, runId: string) => {
			try {
				const run = await cancelRun(workflowId, runId);
				await loadRuns(workflowId);
				if (isTerminalRunStatus(run.status)) {
					markRunning(workflowId, false);
					await refreshWorkflows();
					// Already settled, so this toast IS the outcome — and the only
					// one the user gets. The untrack comes AFTER both refetches on
					// purpose: each of them adopts what it reads, and a list still
					// serving the pre-cancel row would re-arm the watch, which would
					// then announce the same cancellation again five seconds later.
					// Untracking last leaves nothing that can.
					untrackRun(workflowId, runId);
					setToast({ text: "Run cancelled.", variant: "info" });
					return;
				}
				// Still running: it stops after the file it is on, then rolls back
				// what it wrote. The watch already polling reports the outcome.
				setToast({
					text: "Cancelling — the run will stop after the file it is reading, and its changes will be rolled back.",
					variant: "info",
				});
			} catch (error) {
				setToast({
					text:
						error instanceof Error
							? error.message
							: "Could not cancel this run.",
					variant: "error",
				});
				// The run may have finished under us; show what is actually true.
				await loadRuns(workflowId);
			}
		},
		[loadRuns, markRunning, refreshWorkflows, untrackRun],
	);

	/**
	 * Set the table to what it showed when this run finished.
	 *
	 * Resolves to whether it happened, because the caller has a table on screen
	 * that is only stale if it did.
	 */
	const restoreVersionNow = useCallback(
		async (workflowId: string, runId: string): Promise<boolean> => {
			try {
				await restoreRunVersion(workflowId, runId);
				await loadRuns(workflowId);
				setToast({
					text: "Restored. The table now shows this run's results.",
					variant: "info",
				});
				return true;
			} catch (error) {
				setToast({
					text:
						error instanceof Error
							? error.message
							: "Could not restore this version.",
					variant: "error",
				});
				// The refusal usually means the history moved; show what is true.
				await loadRuns(workflowId);
				return false;
			}
		},
		[loadRuns],
	);

	/**
	 * Watched runs settled somewhere above this page.
	 *
	 * Both of this page's copies of the truth are stale the moment that happens:
	 * the workflow list carries `last_run`, and the notifier stops overlaying its
	 * snapshot once a run is no longer tracked. So refetch both — the workflow
	 * list, and the run history of EVERY workflow that settled. Draining by
	 * cursor rather than reacting to "the latest" is what keeps the first of two
	 * runs that finish in the same tick from being skipped.
	 */
	const settlementCursor = useRef(0);
	useEffect(() => {
		const fresh = settlements.filter(
			(settled) => settled.count > settlementCursor.current,
		);
		if (fresh.length === 0) return;
		settlementCursor.current = fresh[fresh.length - 1].count;
		// Background refreshes: a failure here leaves the last good view in place
		// rather than throwing an unhandled rejection at a user who did nothing.
		void refreshWorkflows().catch(() => {});
		for (const workflowId of new Set(fresh.map((s) => s.workflowId))) {
			void loadRuns(workflowId).catch(() => {});
		}
	}, [settlements, refreshWorkflows, loadRuns]);

	/**
	 * Come back to a fresh page, not to whatever was true when you left it
	 * (DOP-276).
	 *
	 * A background tab's timers are throttled and a hidden one may not have
	 * ticked in minutes, so returning is exactly when the page is most stale.
	 * Only the workflow LIST is refetched here: the histories of workflows that
	 * are actually live belong to the provider's watches, which reconcile on the
	 * same events and whose rows this hook renders through the overlay below.
	 * Refetching them here as well would put two identical reads on the wire for
	 * every tab switch.
	 *
	 * `focus` and `visibilitychange` both fire on a tab switch; the hidden guard
	 * and the request token above collapse the pair into one useful refresh.
	 */
	const focusRefreshInFlight = useRef(false);
	useEffect(() => {
		const onReturn = () => {
			if (typeof document !== "undefined" && document.hidden) return;
			if (focusRefreshInFlight.current) return;
			focusRefreshInFlight.current = true;
			// Background refresh: nobody asked for it, so a failure leaves the
			// last good list on screen rather than throwing at the user.
			void refreshWorkflows()
				.catch(() => {})
				.finally(() => {
					focusRefreshInFlight.current = false;
				});
		};
		window.addEventListener("focus", onReturn);
		document.addEventListener("visibilitychange", onReturn);
		return () => {
			window.removeEventListener("focus", onReturn);
			document.removeEventListener("visibilitychange", onReturn);
		};
	}, [refreshWorkflows]);

	/**
	 * What a run history renders.
	 *
	 * The notifier's copy wins ONLY while it is still watching that workflow —
	 * it polls and this hook does not, so during a run its snapshot is the
	 * fresher of the two. Once the run settles the overlay has to stop: a
	 * snapshot frozen at settle time would otherwise mask every later `loadRuns`
	 * result forever, and the history would stop moving.
	 */
	const runsByWorkflow = useMemo(() => {
		const merged: Record<string, WorkflowRun[]> = { ...loadedRuns };
		for (const workflowId of trackedWorkflowIds) {
			const watched = notifierRuns[workflowId];
			if (watched) merged[workflowId] = watched;
		}
		return merged;
	}, [loadedRuns, notifierRuns, trackedWorkflowIds]);

	/**
	 * Locally-pending starts, plus every run the notifier still holds as active
	 * — including runs it adopted at mount and runs it could not confirm as
	 * finished. A workflow is only "not running" once something proved it.
	 */
	const runningIds = useMemo(() => {
		const merged = new Set(pendingRunIds);
		for (const id of trackedWorkflowIds) merged.add(id);
		return merged;
	}, [pendingRunIds, trackedWorkflowIds]);

	return {
		metricGroups,
		folders,
		workflows,
		runsByWorkflow,
		isLoading,
		foldersLoading,
		foldersError,
		reloadFolders: loadFolders,
		isSaving,
		runningIds,
		loadingRunsFor,
		/**
		 * The notifier has not yet been able to say what is already running, so
		 * the page must not offer to start anything.
		 */
		isCheckingActivity: isHydrating,
		loadError,
		toast,
		dismissToast: useCallback(() => setToast(null), []),
		createWorkflow,
		runNow,
		cancelRunNow,
		restoreVersionNow,
		loadRuns,
		reload,
	};
}
